import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "@amber/access/ui";
import { roleAtLeast } from "@amber/contracts";
import { ProfileHistory, appearancesQuery, type LifecycleKind } from "@amber/profiles/ui";
import { ProfileSourcesTab, personPlanQuery, sourceOptionsQuery, type CoverageWindow } from "@amber/collection/ui";
import type { ProfileHistoryCategory } from "@amber/profiles/contracts";
import { Callout, TabList, TabPanel, errorText } from "@amber/ui";
import { profilePageState, type ProfilePanel } from "./pageState";
import { ProfileHeader } from "./ProfileHeader";
import { ProfilePanels } from "./ProfilePanels";
import { profileQuery, rosterQuery } from "./queries";
import type { ProfilesSearch } from "./search";
import { IdentityTab } from "./tabs/IdentityTab";
import { ItemsTab } from "./tabs/ItemsTab";
import { OverviewTab } from "./tabs/OverviewTab";

type ProfileTab = NonNullable<ProfilesSearch["tab"]>;

export function ProfilePage({
  personId,
  tab,
  history,
  coverage,
  onSearch,
}: {
  personId: string;
  tab: ProfileTab;
  history: ProfileHistoryCategory;
  coverage: CoverageWindow;
  onSearch: (patch: Partial<ProfilesSearch>) => void;
}) {
  const session = useSession();
  const editor = session ? roleAtLeast(session.user.role, "editor") : false;
  const detail = useQuery(profileQuery(personId));
  const resolvedId = detail.data?.id ?? personId;
  const roster = useQuery(rosterQuery).data?.people ?? [];
  const appearances = useQuery(appearancesQuery(resolvedId)).data;
  const plan = useQuery(personPlanQuery(resolvedId)).data?.plan ?? null;
  const options = useQuery(sourceOptionsQuery).data?.sources;
  const [panel, setPanel] = useState<ProfilePanel | null>(null);
  const [lifecycle, setLifecycle] = useState<LifecycleKind | null>(null);
  const rosterRow = roster.find((r) => r.id === resolvedId);
  if (detail.isError) {
    return (
      <div className="p-6">
        <Callout tone="attention" title="Couldn't open this profile">
          {errorText(detail.error)}
        </Callout>
      </div>
    );
  }
  const p = detail.data;
  if (!p) return <div className="p-6 text-md text-muted">Loading profile…</div>;
  const state = profilePageState(p, plan, editor);
  const first = state.first;
  const tabs = [
    { value: "overview" as const, label: "Overview" },
    {
      value: "sources" as const,
      label: "Sources",
      ...(first && first.preselected.length + first.suggestions.length > 0
        ? {
            count: `${String(first.preselected.length + first.suggestions.length)} new`,
          }
        : {}),
    },
    { value: "items" as const, label: "Items" },
    { value: "identity" as const, label: "Identity" },
    { value: "history" as const, label: "History" },
  ];
  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <ProfileHeader
          p={p}
          plan={plan}
          state={state}
          editor={editor}
          row={rosterRow}
          appearances={appearances?.count}
          onPanel={setPanel}
          onLifecycle={setLifecycle}
        />
        <TabList
          label="Profile sections"
          value={tab}
          items={tabs}
          onChange={(next) => {
            onSearch({ tab: next });
          }}
          className="mt-3"
        >
          <TabPanel value="overview" className="overflow-auto px-4 pb-6 text-ui">
            <OverviewTab
              p={p}
              state={state}
              editor={editor}
              appearances={appearances?.count}
              coverage={coverage}
              onCoverage={(next) => {
                onSearch({ window: next === 90 ? undefined : next });
              }}
              onPanel={setPanel}
              onLifecycle={setLifecycle}
            />
          </TabPanel>
          <TabPanel value="sources">
            <ProfileSourcesTab
              profile={p}
              plan={first}
              onReview={() => {
                setPanel("review");
              }}
            />
          </TabPanel>
          <TabPanel value="items">
            <ItemsTab profile={p} />
          </TabPanel>
          <TabPanel value="identity">
            <IdentityTab
              profile={p}
              editReason={state.editReason}
              onEdit={() => {
                setPanel("identity");
              }}
              onRefresh={() => {
                setPanel("refresh");
              }}
            />
          </TabPanel>
          <TabPanel value="history">
            <ProfileHistory
              profile={p}
              canEdit={editor}
              filter={history}
              onFilter={(next) => {
                onSearch({ history: next === "all" ? undefined : next });
              }}
            />
          </TabPanel>
        </TabList>
      </div>
      <ProfilePanels
        p={p}
        first={first}
        panel={panel}
        lifecycle={lifecycle}
        rosterRow={rosterRow}
        roster={roster}
        plan={plan}
        options={options}
        onClosePanel={() => {
          setPanel(null);
        }}
        onCloseLifecycle={() => {
          setLifecycle(null);
        }}
      />
    </div>
  );
}
